"use client";
import apolloClient from "@/common/apollo/apolloClient";
import ProtectedRoute from "@/common/components/ProtectedRoute";
import AppSidebar from "@/components/layout/AppSidebar";
import InitUser from "@/common/hooks/InitUser";
import { ThemeProvider, ThemeScript } from "@/common/theme";
import { ApolloProvider } from "@apollo/client";
import { Inter, Bricolage_Grotesque, Space_Mono } from "next/font/google";
import { usePathname } from "next/navigation";
import NextTopLoader from "nextjs-toploader";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { Toaster } from "sonner";
import { persistor, store } from "../store/store";
import "./globals.css";
import { metadata } from "./metadata";

const inter = Inter({
  subsets: ["latin"],
  variable: "--font-sans",
});

const bricolage = Bricolage_Grotesque({
  subsets: ["latin"],
  variable: "--font-display",
});

const spaceMono = Space_Mono({
  subsets: ["latin"],
  weight: ["400", "700"],
  variable: "--font-mono",
});

export default function RootLayout({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const pathname = usePathname();

  const dashboardPaths = ["/dashboard", "/projects", "/reports", "/pm"];
  const isDashboardPath = dashboardPaths.some((path) =>
    pathname.startsWith(path),
  );

  // Worker/foreman routes are fixed-height app shells that scroll inside
  // <main>. The `app-shell` class locks the document (see globals.css).
  const appShellPaths = ["/worker", "/foreman"];
  const isAppShell = appShellPaths.some((path) => pathname.startsWith(path));

  return (
    // suppressHydrationWarning is required because the inline ThemeScript
    // mutates <html>'s class before React hydrates. Critically, <html>'s
    // React-controlled className must stay constant across navigations —
    // any prop change here causes React to overwrite the attribute and
    // strip the `dark`/`light` class ThemeScript added. The `app-shell`
    // toggle lives on <body> for that reason; globals.css uses
    // `html:has(body.app-shell)` to lock the document element.
    <html
      lang="en"
      className={`${inter.variable} ${bricolage.variable} ${spaceMono.variable} ${inter.className}`}
      suppressHydrationWarning
    >
      <head>
        <meta name="title" content={String(metadata.title)} />
        <meta name="description" content={String(metadata.description)} />
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
        <ThemeScript />
      </head>
      <body className={isAppShell ? "app-shell" : undefined}>
        {/* Fires the instant a link is clicked, giving immediate "it's
            loading" feedback during route compilation (dev) and data/chunk
            waits (prod) — before the destination's loading.tsx can render. */}
        <NextTopLoader color="var(--primary)" showSpinner={false} height={3} />
        <Provider store={store}>
          {/* PersistGate delays rendering until the persisted auth/user state
              has rehydrated from localStorage. `loading={null}` keeps the
              SSR'd HTML on screen so users don't see an unauth flash. */}
          <PersistGate loading={null} persistor={persistor}>
            <ApolloProvider client={apolloClient}>
              <ThemeProvider>
                <InitUser />
                <ProtectedRoute>
                  {isDashboardPath ? (
                    <div className="flex h-screen overflow-hidden bg-background text-foreground">
                      <AppSidebar />
                      <main className="flex-1 overflow-y-auto">
                        {children}
                      </main>
                    </div>
                  ) : (
                    <>{children}</>
                  )}
                </ProtectedRoute>
              </ThemeProvider>
            </ApolloProvider>
          </PersistGate>
        </Provider>
        <Toaster />
      </body>
    </html>
  );
}
